import { MinusSquareFilled } from '@ant-design/icons';
import { Input, message, Modal, ModalProps, Space, Typography } from 'antd';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import { RequestJobPostingDetail, RequestJobPostingStageWithApprover } from '../../types';
import Form from '../Form';
import ConfirmRequestJobPostingRejectModal from './ConfirmRequestJobPostingRejectModal';

interface RejectRequestJobPostingFormValues {
  comment?: string;
}

export interface RejectRequestJobPostingModalProps extends ModalProps {
  requestJobPostingId: RequestJobPostingDetail['id'];
  currentStage: RequestJobPostingStageWithApprover;
  onSuccess?: (
    data: void,
    variables: RejectRequestJobPostingFormValues,
    context: unknown
  ) => void | Promise<unknown>;
}

const RejectRequestJobPostingModal = ({
  requestJobPostingId,
  currentStage,
  onSuccess,
  ...props
}: RejectRequestJobPostingModalProps) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [openConfirmRequestJobPostingReject, setOpenConfirmRequestJobPostingReject] =
    useState(false);

  const requestJobPosting = useAPIQuery(`/request-job-postings/${requestJobPostingId}/`);

  const mutation = useAPIMutation(
    async ({ comment }: RejectRequestJobPostingFormValues, api) => {
      await api.post(`/request-job-posting-stage-actions/${currentStage.id}/reject/`, {
        comment: comment || '',
      });
    },
    {
      onSettled: async () => {
        await queryClient.invalidateQueries('/request-job-postings/');
      },
      onSuccess: async (data, variables, context) => {
        message.success(t('Requisição reprovada.'));

        onSuccess && onSuccess(data, variables, context);
      },
      onError: (error, variables, context) => {
        message.error(error.message);
      },
    }
  );

  return (
    <>
      <ConfirmRequestJobPostingRejectModal
        key={`ConfirmRequestJobPostingRejectModal-${requestJobPostingId}`}
        requestJobPostingName={requestJobPosting.data?.name || ''}
        open={openConfirmRequestJobPostingReject}
        onOk={() => {
          form.submit();
          setOpenConfirmRequestJobPostingReject(false);
        }}
        onCancel={() => setOpenConfirmRequestJobPostingReject(false)}
      />
      <Modal
        title={
          <Typography.Text strong>
            <Trans>Reprovar requisição</Trans>
          </Typography.Text>
        }
        okButtonProps={{
          htmlType: 'submit',
          danger: true,
          icon: <MinusSquareFilled />,
          loading: mutation.isLoading,
        }}
        okText="Reprovar"
        onOk={() => {
          setOpenConfirmRequestJobPostingReject(true);
        }}
        {...props}
      >
        <Helmet>
          <title>
            {t('Reprovar requisição')} | {t('Recrutamento e Seleção')}
          </title>
        </Helmet>
        <Space direction="vertical">
          <Typography.Text>
            <Trans>Ao confirmar, a requisição</Trans>{' '}
            <Typography.Text strong>{requestJobPosting.data?.name}</Typography.Text>
            <Trans>será reprovada e finalizada.</Trans>
          </Typography.Text>
          <Form<RejectRequestJobPostingFormValues>
            form={form}
            layout="vertical"
            onFinish={async (values) => {
              mutation.mutate(values);
            }}
          >
            <Form.Item label={t('Comentários')} name="comment">
              <Input.TextArea maxLength={4000} rows={4} showCount />
            </Form.Item>
          </Form>
        </Space>
      </Modal>
    </>
  );
};

export default RejectRequestJobPostingModal;
